/**
 * The name the core views give a project's application — « Ouvrir Odoo »,
 * « Odoo prêt », « projet Odoo » —: the `title` of its `AppType`
 * descriptor, so that another type is named by its own (step 9f of the
 * platform plan).
 *
 * For an Odoo project (`LEGACY_APP_TYPE`), and for a project whose type is
 * not known yet (undefined: its entity or Application unread), the legacy
 * name `LEGACY_APP_TITLE` while the descriptor loads, and for good when it
 * cannot be read: an Odoo text never shows another word first. For another
 * type without a readable descriptor, its `AppType` name.
 *
 * Read through the descriptors' cache of `templateRoles.ts` (one request per
 * type every 30 s for the whole app, shared with `useProjectTemplates`).
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import type { AppTypeDetail } from './appTypes';
import { LEGACY_APP_TITLE, LEGACY_APP_TYPE } from './projectIdentity';
import {
  declaredTemplates,
  embeddedOwnerOf,
  loadAppType,
} from './templateRoles';

/** The name of `appType` before, or without, its descriptor. */
export function fallbackAppTitle(appType: string | undefined): string {
  return appType === undefined || appType === LEGACY_APP_TYPE
    ? LEGACY_APP_TITLE
    : appType;
}

/** The name of a project of `appType` (header). */
export function useAppTitle(appType: string | undefined): string {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [loaded, setLoaded] = useState<{ name: string; title: string }>();

  useEffect(() => {
    if (appType === undefined) return undefined;
    let active = true;
    loadAppType({ fetchApi, discoveryApi }, appType).then(
      found => {
        if (active && found) setLoaded({ name: appType, title: found.title });
      },
      () => {
        // Unreadable descriptor: the fallback name stays.
      },
    );
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi, appType]);

  return loaded && loaded.name === appType
    ? loaded.title
    : fallbackAppTitle(appType);
}

/** Ce qu'un appelant lit d'un type qui désigne un modèle. */
export type TemplateDeclarer = Pick<AppTypeDetail, 'name' | 'title'> & {
  spec: unknown;
};

/**
 * The application types among `appTypes` that designate the template `ref`;
 * aucun ne le désignant, le descripteur Odoo embarqué s'il le désigne en
 * propre (`embeddedOwnerOf` : la création Odoo, catalogue sans `AppType
 * odoo` ou descripteurs pas encore lus).
 */
export function declarersOf(
  ref: string | undefined,
  appTypes: readonly AppTypeDetail[],
): TemplateDeclarer[] {
  if (ref === undefined) return [];
  const wanted = ref.toLowerCase();
  const declarers: TemplateDeclarer[] = appTypes.filter(appType =>
    Object.values(declaredTemplates(appType)).includes(wanted),
  );
  if (declarers.length > 0) return declarers;
  const embedded = embeddedOwnerOf(ref, appTypes);
  return embedded ? [embedded] : [];
}

/**
 * The name of the application a task of the template `ref` is about, when
 * the template alone says it: the title of the one type that designates it
 * (`declarersOf`, the embedded Odoo descriptor's for the Odoo creation
 * before or without the descriptors).
 * Undefined when none or several do (a generic template): the project's
 * type decides then.
 */
export function templateAppTitle(
  ref: string | undefined,
  appTypes: readonly AppTypeDetail[],
): string | undefined {
  const declarers = declarersOf(ref, appTypes);
  return declarers.length === 1 ? declarers[0].title : undefined;
}

/**
 * « qu’Odoo », « que Nextcloud » : French elides `que` before a vowel, for
 * the messages that put the name after it (`{{thatApp}}`). A title starting
 * with an `h` keeps `que`, whether it is aspirated depending on the word.
 */
export function thatTitle(title: string): string {
  return /^[aeiouàâäéèêëîïôöùûü]/i.test(title) ? `qu’${title}` : `que ${title}`;
}

/**
 * The parameters of a message naming the application: `app`, and
 * `thatApp` (`thatTitle`) for the French messages that put it after `que`
 * (« jusqu’à ce qu’Odoo soit prêt »). The English message does not name
 * `thatApp`; a variable, rather than an object literal, lets the call pass
 * it all the same.
 */
export function appMessageParams(title: string): {
  app: string;
  thatApp: string;
} {
  return { app: title, thatApp: thatTitle(title) };
}
